import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { processGoogleSession } from '../services/api';
import { toast } from 'sonner';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function GoogleLoginButton({ mode = 'login' }) {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const popupRef = useRef(null);
  const checkIntervalRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // Check for session_id in URL on mount (for redirect-based fallback)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const hashParams = new URLSearchParams(hash.replace('#', ''));
      const sessionId = hashParams.get('session_id');
      if (sessionId) {
        handleSessionId(sessionId);
        // Clean up URL
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, []);

  const handleSessionId = async (sessionId) => {
    setIsLoading(true);
    try {
      const response = await processGoogleSession(sessionId);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      setToken(token);
      setUser(user);

      toast.success(`Welcome${user.username ? ', ' + user.username : ''}!`);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Google auth failed:', error);
      toast.error('Google sign-in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Calculate popup position (centered)
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Create callback URL
    const callbackUrl = window.location.origin + '/auth/callback';
    
    // Try popup first
    const popup = window.open(
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(callbackUrl)}`,
      'Google Sign In',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    // If popup blocked, fallback to redirect
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      toast.info('Opening Google sign-in...');
      // Redirect-based fallback - redirects back to current page with session_id
      const redirectUrl = window.location.origin + window.location.pathname;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }

    popupRef.current = popup;
    setIsLoading(true);

    // Monitor popup
    checkIntervalRef.current = setInterval(async () => {
      try {
        if (!popup || popup.closed) {
          clearInterval(checkIntervalRef.current);
          setIsLoading(false);
          return;
        }

        let popupUrl;
        try {
          popupUrl = popup.location.href;
        } catch (e) {
          return; // Cross-origin, still on Google
        }

        // Check if back on our domain
        if (popupUrl && popupUrl.includes(window.location.origin)) {
          // Check for session_id in hash
          if (popupUrl.includes('session_id=')) {
            clearInterval(checkIntervalRef.current);
            popup.close();

            const hashPart = popupUrl.split('#')[1] || '';
            const hashParams = new URLSearchParams(hashPart);
            const sessionId = hashParams.get('session_id');

            if (sessionId) {
              await handleSessionId(sessionId);
            } else {
              setIsLoading(false);
              toast.error('Sign-in failed. Please try again.');
            }
          }
        }
      } catch (e) {
        // Ignore errors during cross-origin checks
      }
    }, 300);
  };

  return (
    <button
      onClick={handleGoogleLogin}
      disabled={isLoading}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg border border-gray-300 transition-colors disabled:opacity-70"
      data-testid="google-login-btn"
    >
      {isLoading ? (
        <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      )}
      {mode === 'login' ? 'Continue with Google' : 'Sign up with Google'}
    </button>
  );
}

export default GoogleLoginButton;
